import React, { useState, useEffect, useCallback } from 'react';
import { 
  MapPin, Clock, Package, CheckCircle2, Truck, Store, 
  Phone, MessageSquare, RefreshCw, Loader2, AlertCircle,
  Navigation, User, CreditCard, Bell, BellOff, Map, ChevronLeft, Scan, Camera
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { ordersAPI, driverLocationAPI } from '../services/api';
import { toast } from 'sonner';
import CameraBarcodeScanner from '../components/CameraBarcodeScanner';

const ORDER_STATUSES = [
  { id: 'pending', label: 'Order Placed', icon: Package, color: 'text-gray-500' },
  { id: 'payment_confirmed', label: 'Payment Confirmed', icon: CreditCard, color: 'text-blue-500' },
  { id: 'picking', label: 'Picking Items', icon: Store, color: 'text-yellow-500' },
  { id: 'picked', label: 'Items Picked', icon: CheckCircle2, color: 'text-orange-500' },
  { id: 'ready_for_delivery', label: 'Ready for Delivery', icon: Package, color: 'text-purple-500' },
  { id: 'assigned', label: 'Driver Assigned', icon: User, color: 'text-indigo-500' },
  { id: 'driver_to_store', label: 'Driver En Route', icon: Navigation, color: 'text-cyan-500' },
  { id: 'driver_at_store', label: 'Driver at Store', icon: MapPin, color: 'text-teal-500' },
  { id: 'out_for_delivery', label: 'Out for Delivery', icon: Truck, color: 'text-blue-600' },
  { id: 'delivered', label: 'Delivered', icon: CheckCircle2, color: 'text-green-600' },
  { id: 'ready_for_pickup', label: 'Ready for Pickup', icon: Store, color: 'text-amber-500' },
  { id: 'picked_up', label: 'Picked Up', icon: CheckCircle2, color: 'text-green-600' },
  { id: 'cancelled', label: 'Cancelled', icon: AlertCircle, color: 'text-red-500' },
];

// Simulated route coordinates for driver (from store to customer)
const SIMULATED_ROUTE = [
  { lat: 43.6532, lon: -79.3832, label: 'Store' },
  { lat: 43.6550, lon: -79.3810 },
  { lat: 43.6575, lon: -79.3785 },
  { lat: 43.6600, lon: -79.3760 },
  { lat: 43.6625, lon: -79.3730 },
  { lat: 43.6650, lon: -79.3700, label: 'Customer' },
];

const OrderTracking = ({ user, onBack }) => {
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  // Push notification state
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [notificationPermission, setNotificationPermission] = useState('default');
  
  // Live driver location state
  const [driverLocation, setDriverLocation] = useState(null);
  const [routeProgress, setRouteProgress] = useState(0);
  const [estimatedTime, setEstimatedTime] = useState(null);
  const [previousStatus, setPreviousStatus] = useState({});

  // QR Scanner state for customer pickup verification
  const [showScanner, setShowScanner] = useState(false);

  // Handle QR scan for customer pickup verification
  const handlePickupScan = async (qrData) => {
    try {
      // Parse QR data - format: "UPLANDS-ORDER:ORDER_NUMBER"
      const match = qrData.match(/UPLANDS-ORDER:(.+)/);
      if (!match) {
        toast.error('Invalid QR code');
        setShowScanner(false);
        return;
      }
      
      const scannedOrderNumber = match[1];
      
      // Check if scanned QR matches selected order
      if (selectedOrder && (selectedOrder.order_number === scannedOrderNumber || selectedOrder.id.includes(scannedOrderNumber.toLowerCase()))) {
        // Verify pickup with backend
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const token = localStorage.getItem('token');
        
        const response = await fetch(`${API_URL}/api/pickup/verify-customer-scan`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            order_id: selectedOrder.id,
            scanned_order_number: scannedOrderNumber
          })
        });
        
        if (response.ok) {
          toast.success('Pickup verified! Please collect your order.');
          setShowScanner(false);
          // Refresh orders to update status
          fetchOrders();
        } else {
          const error = await response.json();
          toast.error(error.detail || 'Verification failed');
        }
      } else {
        toast.error('QR code does not match your order');
      }
      
      setShowScanner(false);
    } catch (error) {
      console.error('Pickup scan error:', error);
      toast.error('Failed to verify pickup');
      setShowScanner(false);
    }
  };

  // Check notification permission on mount
  useEffect(() => {
    if ('Notification' in window) {
      setNotificationPermission(Notification.permission);
      setNotificationsEnabled(Notification.permission === 'granted');
    }
  }, []);

  // Request notification permission
  const requestNotificationPermission = async () => {
    if (!('Notification' in window)) {
      toast.error('Push notifications are not supported in this browser');
      return;
    }

    try {
      const permission = await Notification.requestPermission();
      setNotificationPermission(permission);
      setNotificationsEnabled(permission === 'granted');
      
      if (permission === 'granted') {
        toast.success('Push notifications enabled!');
        // Send a test notification
        new Notification('Notifications Enabled', {
          body: 'You will now receive updates about your orders.',
          icon: '/favicon.ico',
          badge: '/favicon.ico'
        });
      } else if (permission === 'denied') {
        toast.error('Notification permission denied');
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      toast.error('Could not enable notifications');
    }
  };

  // Send push notification
  const sendNotification = useCallback((title, body, tag = null) => {
    if (notificationsEnabled && 'Notification' in window && Notification.permission === 'granted') {
      new Notification(title, {
        body,
        icon: '/favicon.ico',
        badge: '/favicon.ico',
        tag: tag || 'order-update',
        renotify: true,
        requireInteraction: false
      });
    }
  }, [notificationsEnabled]);

  // Monitor order status changes for notifications
  useEffect(() => {
    if (!selectedOrder || !notificationsEnabled) return;

    const orderId = selectedOrder.id;
    const currentStatus = selectedOrder.status;
    const prevStatus = previousStatus[orderId];

    if (prevStatus && prevStatus !== currentStatus) {
      const statusInfo = ORDER_STATUSES.find(s => s.id === currentStatus);
      if (statusInfo) {
        sendNotification(
          `Order Update: ${statusInfo.label}`,
          `Your order #${orderId.slice(0, 8).toUpperCase()} status has changed to "${statusInfo.label}"`,
          `order-${orderId}`
        );
        toast.info(`Order status updated to: ${statusInfo.label}`);
      }
    }

    setPreviousStatus(prev => ({ ...prev, [orderId]: currentStatus }));
  }, [selectedOrder?.status, selectedOrder?.id, notificationsEnabled, sendNotification, previousStatus]);

  // Simulate live driver location updates
  useEffect(() => {
    if (!selectedOrder || selectedOrder.status !== 'out_for_delivery') {
      setDriverLocation(null);
      setRouteProgress(0);
      return;
    }

    // Start driver simulation
    setDriverLocation(SIMULATED_ROUTE[0]);
    
    const simulateMovement = setInterval(() => {
      setRouteProgress(prev => {
        const next = prev + 1;
        if (next >= SIMULATED_ROUTE.length) {
          clearInterval(simulateMovement);
          sendNotification(
            'Driver Arriving!',
            'Your driver is almost at your location. Please be ready!',
            'driver-arriving'
          );
          return SIMULATED_ROUTE.length - 1;
        }
        
        setDriverLocation(SIMULATED_ROUTE[next]);
        
        // Calculate estimated time (mock)
        const remainingStops = SIMULATED_ROUTE.length - next - 1;
        setEstimatedTime(remainingStops * 3); // 3 mins per stop
        
        return next;
      });
    }, 5000); // Update every 5 seconds

    return () => clearInterval(simulateMovement);
  }, [selectedOrder?.status, selectedOrder?.id, sendNotification]);

  useEffect(() => {
    fetchOrders();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchOrders, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchOrders = async () => {
    try {
      const data = await ordersAPI.getMyOrders();
      // Filter to only show active orders (not delivered/cancelled)
      const activeOrders = data.filter(order => 
        !['delivered', 'cancelled', 'completed'].includes(order.status?.toLowerCase())
      );
      setOrders(activeOrders);
      
      // Auto-select first order if none selected
      if (!selectedOrder && activeOrders.length > 0) {
        setSelectedOrder(activeOrders[0]);
      } else if (selectedOrder) {
        // Update selected order with fresh data
        const updated = activeOrders.find(o => o.id === selectedOrder.id);
        if (updated) setSelectedOrder(updated);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchOrders();
    toast.success('Order status updated');
  };

  const getStatusIndex = (status) => {
    const index = ORDER_STATUSES.findIndex(s => 
      s.id === status?.toLowerCase() || s.label.toLowerCase() === status?.toLowerCase()
    );
    return index >= 0 ? index : 0;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-16 text-center">
        <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-800 mb-2">No Active Orders</h2>
        <p className="text-gray-500 mb-4">You don&apos;t have any orders in progress right now.</p>
        <Button onClick={() => window.location.reload()} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      {/* Back Button */}
      {onBack && (
        <Button 
          variant="ghost" 
          onClick={onBack}
          className="mb-4 flex items-center gap-1 text-gray-600 hover:text-gray-900"
        >
          <ChevronLeft className="w-5 h-5" />
          Back to Orders
        </Button>
      )}
      
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
            <MapPin className="w-8 h-8 text-green-600" />
            Live Order Tracking
          </h1>
          <p className="text-gray-500 mt-1">Track your orders in real-time</p>
        </div>
        <div className="flex items-center gap-4">
          {/* Notification Toggle */}
          <div className="flex items-center gap-2 bg-gray-100 px-3 py-2 rounded-lg">
            {notificationsEnabled ? (
              <Bell className="w-4 h-4 text-green-600" />
            ) : (
              <BellOff className="w-4 h-4 text-gray-400" />
            )}
            <Label htmlFor="notifications" className="text-sm text-gray-700">
              Notifications
            </Label>
            <Switch
              id="notifications"
              checked={notificationsEnabled}
              onCheckedChange={(checked) => {
                if (checked && notificationPermission !== 'granted') {
                  requestNotificationPermission();
                } else {
                  setNotificationsEnabled(checked);
                }
              }}
              data-testid="notification-toggle"
            />
          </div>
          
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={refreshing}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Order List */}
        <div className="lg:col-span-1 space-y-4">
          <h3 className="font-semibold text-gray-700">Active Orders ({orders.length})</h3>
          {orders.map((order) => (
            <Card 
              key={order.id}
              className={`cursor-pointer transition-all hover:shadow-md ${
                selectedOrder?.id === order.id ? 'ring-2 ring-green-500 bg-green-50' : ''
              }`}
              onClick={() => setSelectedOrder(order)}
              data-testid={`order-card-${order.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-mono font-bold text-blue-700">
                    {order.order_number || `#${order.id?.slice(0, 8).toUpperCase()}`}
                  </span>
                  <Badge className={`${
                    order.status === 'out_for_delivery' ? 'bg-blue-100 text-blue-700' :
                    order.status === 'picking' ? 'bg-orange-100 text-orange-700' :
                    order.status === 'ready_for_pickup' ? 'bg-purple-100 text-purple-700' :
                    order.status === 'payment_confirmed' ? 'bg-green-100 text-green-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {ORDER_STATUSES.find(s => s.id === order.status)?.label || order.status}
                  </Badge>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">
                    {order.items?.length || 0} items • {order.order_type === 'pickup' ? '🏪 Pickup' : '🚚 Delivery'}
                  </span>
                  <span className="font-semibold text-gray-800">
                    ${(order.total || 0).toFixed(2)}
                  </span>
                </div>
                <p className="text-xs text-gray-400 mt-2">
                  {formatDate(order.created_at)}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Order Details & Tracking */}
        {selectedOrder && (
          <div className="lg:col-span-2 space-y-6">
            {/* Status Timeline */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Navigation className="w-5 h-5 text-green-600" />
                  Order Status
                  <Badge variant="outline" className="ml-2">
                    {selectedOrder.order_type === 'pickup' ? '🏪 Pickup' : '🚚 Delivery'}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="relative">
                  {(() => {
                    // Show different status flow based on order type
                    const isPickup = selectedOrder.order_type === 'pickup';
                    const deliveryFlow = ['pending', 'payment_confirmed', 'picking', 'picked', 'ready_for_delivery', 'assigned', 'out_for_delivery', 'delivered'];
                    const pickupFlow = ['pending', 'payment_confirmed', 'picking', 'picked', 'ready_for_pickup', 'picked_up'];
                    const statusFlow = isPickup ? pickupFlow : deliveryFlow;
                    
                    return statusFlow.map((statusId, index) => {
                      const status = ORDER_STATUSES.find(s => s.id === statusId);
                      if (!status) return null;
                      
                      const currentStatusIndex = statusFlow.indexOf(selectedOrder.status);
                      const isCompleted = index < currentStatusIndex;
                      const isCurrent = index === currentStatusIndex;
                      const StatusIcon = status.icon;
                      
                      return (
                        <div key={status.id} className="flex items-start mb-6 last:mb-0">
                          <div className="relative">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              isCompleted ? 'bg-green-500 text-white' :
                              isCurrent ? 'bg-blue-500 text-white animate-pulse' :
                              'bg-gray-200 text-gray-400'
                            }`}>
                              <StatusIcon className="w-5 h-5" />
                            </div>
                            {index < statusFlow.length - 1 && (
                              <div className={`absolute left-5 top-10 w-0.5 h-6 ${
                                isCompleted ? 'bg-green-500' : 'bg-gray-200'
                              }`} />
                            )}
                          </div>
                          <div className="ml-4">
                            <p className={`font-medium ${
                              isCompleted || isCurrent ? 'text-gray-800' : 'text-gray-400'
                            }`}>
                              {status.label}
                            </p>
                            {isCurrent && (
                              <p className="text-sm text-blue-600 mt-1">
                                Current status
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              </CardContent>
            </Card>

            {/* Live Driver Location Map */}
            {selectedOrder.status === 'out_for_delivery' && (
              <Card className="bg-gradient-to-br from-blue-50 to-green-50 overflow-hidden">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Map className="w-5 h-5 text-blue-600" />
                    Live Driver Location
                    <Badge className="bg-green-500 text-white animate-pulse ml-2">LIVE</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  {/* Simulated Map View */}
                  <div className="relative bg-white rounded-lg border border-gray-200 h-48 overflow-hidden mb-4">
                    {/* Map Background */}
                    <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200">
                      {/* Grid lines to simulate map */}
                      <div className="absolute inset-0 opacity-20" style={{
                        backgroundImage: 'linear-gradient(rgba(0,0,0,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.1) 1px, transparent 1px)',
                        backgroundSize: '30px 30px'
                      }} />
                    </div>
                    
                    {/* Route Path */}
                    <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="none">
                      <defs>
                        <linearGradient id="routeGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor="#3B82F6" />
                          <stop offset="100%" stopColor="#22C55E" />
                        </linearGradient>
                      </defs>
                      <path 
                        d="M 30 170 C 80 140, 120 100, 180 80 S 280 50, 340 30" 
                        stroke="url(#routeGradient)" 
                        strokeWidth="4" 
                        fill="none"
                        strokeDasharray="8,4"
                        opacity="0.6"
                      />
                      <path 
                        d={`M 30 170 C 80 140, 120 100, 180 80 S 280 50, 340 30`}
                        stroke="#3B82F6" 
                        strokeWidth="4" 
                        fill="none"
                        strokeDasharray={`${routeProgress * 60} 400`}
                      />
                    </svg>

                    {/* Store Marker */}
                    <div className="absolute left-4 bottom-4 flex items-center gap-2">
                      <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center shadow-lg">
                        <Store className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-xs font-medium bg-white px-2 py-1 rounded shadow">Store</span>
                    </div>

                    {/* Customer Marker */}
                    <div className="absolute right-4 top-4 flex items-center gap-2">
                      <span className="text-xs font-medium bg-white px-2 py-1 rounded shadow">You</span>
                      <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center shadow-lg">
                        <MapPin className="w-4 h-4 text-white" />
                      </div>
                    </div>

                    {/* Driver Marker (animated) */}
                    {driverLocation && (
                      <div 
                        className="absolute transition-all duration-1000 ease-out"
                        style={{
                          left: `${20 + (routeProgress / (SIMULATED_ROUTE.length - 1)) * 70}%`,
                          top: `${80 - (routeProgress / (SIMULATED_ROUTE.length - 1)) * 65}%`,
                          transform: 'translate(-50%, -50%)'
                        }}
                      >
                        <div className="relative">
                          <div className="absolute -inset-4 bg-blue-400 rounded-full animate-ping opacity-30" />
                          <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center shadow-xl border-2 border-white">
                            <Truck className="w-5 h-5 text-white" />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Driver Info */}
                  <div className="flex items-center justify-between bg-white rounded-lg p-3 border border-gray-200">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                        <User className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-800">Driver on the way</p>
                        <p className="text-sm text-gray-500">
                          {estimatedTime ? `${estimatedTime} mins away` : 'Calculating...'}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline">
                        <Phone className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="outline">
                        <MessageSquare className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="mt-4">
                    <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                      <span>Store</span>
                      <span>Your location</span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-blue-500 to-green-500 transition-all duration-1000"
                        style={{ width: `${(routeProgress / (SIMULATED_ROUTE.length - 1)) * 100}%` }}
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Pickup QR Code & Code - For Store Pickup Orders */}
            {selectedOrder.order_type === 'pickup' && (selectedOrder.qr_code || selectedOrder.pickup_code) && (
              <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-amber-800">
                    <Store className="w-5 h-5" />
                    Store Pickup
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col md:flex-row items-center gap-6">
                    {/* QR Code */}
                    {selectedOrder.qr_code && (
                      <div className="text-center">
                        <img 
                          src={selectedOrder.qr_code} 
                          alt="Pickup QR Code" 
                          className="w-40 h-40 border-4 border-white rounded-xl shadow-lg"
                        />
                        <p className="text-sm text-amber-700 mt-2">Show this at the pickup counter</p>
                      </div>
                    )}
                    
                    {/* Pickup Code */}
                    <div className="flex-1 text-center md:text-left">
                      <p className="text-sm text-amber-600 mb-1">Your Pickup Code</p>
                      <p className="text-4xl font-bold text-amber-800 tracking-[0.3em] bg-white py-3 px-6 rounded-xl shadow-inner">
                        {selectedOrder.pickup_code || 'N/A'}
                      </p>
                      <p className="text-sm text-amber-600 mt-3">
                        Show this code or scan the QR at the pickup counter
                      </p>
                      <div className="mt-4 p-3 bg-amber-100 rounded-lg">
                        <p className="text-xs text-amber-700">
                          <strong>Order Number:</strong> {selectedOrder.order_number || `#${selectedOrder.id?.slice(0, 8).toUpperCase()}`}
                        </p>
                      </div>
                      
                      {/* Scan to Verify Button - for ready_for_pickup orders */}
                      {selectedOrder.status === 'ready_for_pickup' && (
                        <Button
                          onClick={() => setShowScanner(true)}
                          className="mt-4 w-full bg-amber-600 hover:bg-amber-700 text-white"
                        >
                          <Camera className="w-4 h-4 mr-2" />
                          Scan Store QR to Verify Pickup
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Camera Scanner for Pickup Verification */}
            <CameraBarcodeScanner
              isOpen={showScanner}
              onClose={() => setShowScanner(false)}
              onScan={(barcode) => {
                handlePickupScan(barcode);
              }}
              products={[]} // No products needed for QR scanning
              onAddToCart={() => {}} // Not used for pickup verification
              mode="scan" // Just scanning mode
            />

            {/* Order Summary */}
            <Card>
              <CardHeader>
                <CardTitle>Order Details</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Items */}
                  <div>
                    <h4 className="font-medium text-gray-700 mb-2">Items</h4>
                    <div className="space-y-2">
                      {selectedOrder.items?.map((item, idx) => (
                        <div key={idx} className="flex justify-between text-sm">
                          <span className="text-gray-600">
                            {item.name} × {item.quantity}
                          </span>
                          <span className="font-medium">
                            ${(item.price * item.quantity).toFixed(2)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="border-t pt-4">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-500">Subtotal</span>
                      <span>${(selectedOrder.subtotal || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-500">Tax</span>
                      <span>${(selectedOrder.tax || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-gray-500">Delivery</span>
                      <span>${(selectedOrder.delivery_fee || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between font-bold text-lg border-t pt-2">
                      <span>Total</span>
                      <span className="text-green-600">${(selectedOrder.total || 0).toFixed(2)}</span>
                    </div>
                  </div>

                  {/* Delivery Address */}
                  {selectedOrder.delivery_info && (
                    <div className="border-t pt-4">
                      <h4 className="font-medium text-gray-700 mb-2 flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        Delivery Address
                      </h4>
                      <p className="text-sm text-gray-600">
                        {selectedOrder.delivery_info.name}<br />
                        {selectedOrder.delivery_info.address}<br />
                        {selectedOrder.delivery_info.city}, {selectedOrder.delivery_info.province}
                      </p>
                    </div>
                  )}

                  {/* Contact */}
                  <div className="flex gap-2 pt-2">
                    <Button variant="outline" size="sm" className="flex-1">
                      <Phone className="w-4 h-4 mr-2" />
                      Call Store
                    </Button>
                    <Button variant="outline" size="sm" className="flex-1">
                      <MessageSquare className="w-4 h-4 mr-2" />
                      Message
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderTracking;
